import React, { useEffect } from 'react';
import {
  BasketButtonsInner,
  SWBakestTotalPrices,
  SWBasketContainer,
  SWBasketTotal,
  WrapperBasketButtons,
} from '../SWBasket.styles';
import { Heading } from '@uk-source-web/heading';
import { Button } from '@uk-source-web/button';
import handleTranslation from '@/helpers/handleTranslation';
import { OPCOOptions } from '@/types/Services.types';
import { checkForZeroOrNull } from '@/helpers/nullOrZero';
import { useAppSelector } from '@/store';
import { SWBasketStripProps } from '../SWBasket.types';
import { useTranslation } from 'react-i18next';
import BasketMoreInformation from './BasketMoreInformation/BasketMoreInformation';
import BasketPriceStrip from './BasketPriceStrip';
import { SWBakestTotalPricesStrip } from './BasketPriceStrip/BasketStrip.styles';
import { useBasketSummary } from '@/contexts/BasketSummaryContext/BasketSummary';
import { translatedMDFValues } from '../translatedValues';

const OPCO = process.env.GATSBY_OPCO;

const BasketStrip = ({
  continueShoppingButton,
  requestCallbackButton,
  goToCheckoutButton,
  postProductOrder,
  disableContinueButton,
  hideContinueShoppingButton,
}: SWBasketStripProps) => {
  const hasUpfrontPrice = OPCO === OPCOOptions.IRELAND;

  const { discountData } = useAppSelector(state => state.userDiscount);
  const [monthlyCost, setMonthlyCost] = React.useState<number | string>(0);

  // Access basket summary from context to get the total prices
  const { basketSummary } = useBasketSummary();

  const { t } = useTranslation();
  const { userBasket } = useAppSelector(({ userBasket: { data } }) => ({
    userBasket: data,
  }));
  const montlyCostValue = checkForZeroOrNull(basketSummary?.data.recurringCharges || userBasket?.total?.monthly?.gross, 'NA');
  const calcMonthlyDiscount = userBasket?.total?.monthly?.gross;
  const calcUpfrontDiscount = userBasket?.total?.upfront?.gross;
  const percentageValue = discountData?.discountValue
    ? parseFloat(discountData?.discountValue.split(':')[1]?.trim().replace('%', '') || '0')
    : 0;
  const upfrontPriceAfterDiscount = discountData ? discountData.dueNow : calcUpfrontDiscount;
  const monthlyPriceAfterDiscount = discountData ? discountData.recurringCharges : calcMonthlyDiscount;

  const checkMontlyCostValue =
    montlyCostValue === 'NA' ? 'NA' : discountData ? monthlyPriceAfterDiscount : userBasket?.total?.monthly?.gross;

  const upfrontCostValue = checkForZeroOrNull(userBasket.total?.upfront?.gross, 'NA');
  const checkUpfrontCostValue =
    upfrontCostValue === 'NA' ? 'NA' : discountData ? upfrontPriceAfterDiscount : userBasket?.total?.upfront?.gross;
  const totalCost = handleTranslation('Total cost', 'Valor total');
  const monthlyTrans = handleTranslation('Monthly', 'Valor mensal');
  const completePurchase = handleTranslation('Complete purchase', 'Completar compra');

  useEffect(() => {
    if(discountData){
      setMonthlyCost(discountData.recurringCharges);
    } else {
      setMonthlyCost(basketSummary?.data.recurringCharges || userBasket?.total?.monthly?.gross || 'NA' );
    }

  }, [basketSummary, discountData]);

  const requestCallbackhandler = (href: string | undefined) => {
    window.utag?.view({
      event_name: 'ui_interaction',
      component_name: 'Click Component',
      event_category: 'Basket',
      event_label: 'Request Callback',
      event_action: 'Basket',
      component_type: 'Button',
    });
    window.location.href = href;
  };

  const upFront = translatedMDFValues.upfront;

  return (
    <SWBasketContainer backgroundColor="#FFFFFF">
      <SWBasketTotal>
        <Heading level={4} weight={3}>
          {totalCost}
        </Heading>
        <SWBakestTotalPricesStrip>
          {checkUpfrontCostValue != 'NA' ? (
            <BasketPriceStrip label={upFront} labelAlign="left" value={upfrontCostValue} />
          ) : (
            <BasketPriceStrip label={upFront} labelAlign="left" value={checkUpfrontCostValue} />
          )}
          <BasketPriceStrip label={monthlyTrans} labelAlign="left" value={monthlyCost} />
        </SWBakestTotalPricesStrip>
      </SWBasketTotal>
      <BasketMoreInformation />
      <WrapperBasketButtons>
        <BasketButtonsInner>
          {!hideContinueShoppingButton && OPCO !== OPCOOptions.PORTUGAL && (
            <Button
              text={continueShoppingButton.label}
              appearance="secondary"
              data-selector="MS365-BundleConfig-OrderSummary-Next"
              href={continueShoppingButton.url}
            />
          )}
          {requestCallbackButton && (
            <Button
              text={requestCallbackButton.label}
              appearance="secondary"
              data-selector="MS365-BundleConfig-OrderSummary-RequestCallback"
              onClick={() => requestCallbackhandler(requestCallbackButton.url)}
            />
          )}
        </BasketButtonsInner>
        <Button
          id="basket-button-complete-purchase"
          state={userBasket && userBasket.items?.length > 0 && !disableContinueButton ? undefined : 'disabled'}
          text={completePurchase}
          appearance={goToCheckoutButton.rank}
          onClick={postProductOrder}
        />
      </WrapperBasketButtons>
    </SWBasketContainer>
  );
};

export default BasketStrip;
